"use strict";
/**
 * Framework-agnostic helpers for live support hours, shared by frontend
 * support dialogs and the backend queue-wait pager.
 *
 * Hour windows are defined as half-open ranges [start, end) in 24-hour
 * Pacific Time. The same numbers feed the frontend availability checks and
 * the backend pager, so the advertised hours never drift from the runtime
 * gate.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isWithinHours = exports.toNumericPartMap = void 0;
const timeZones_1 = require("./timeZones");
const PACIFIC_WEEKDAY_SHORT = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
const PACIFIC_WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZones_1.PACIFIC_TIME_ZONE,
    weekday: 'short',
});
const PACIFIC_HOUR_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZones_1.PACIFIC_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
/**
 * Collapses Intl.DateTimeFormat parts into a `{ type: numericValue }` map so
 * downstream code can pluck fields with dictionary lookups instead of a
 * closure over the parts array.
 */
const toNumericPartMap = (parts) => {
    const map = {};
    for (const part of parts) {
        const partValue = Number(part.value);
        if (Number.isFinite(partValue)) {
            map[part.type] = partValue;
        }
    }
    return map;
};
exports.toNumericPartMap = toNumericPartMap;
/** Current weekday (0=Sun … 6=Sat) in Pacific Time. DST is handled by Intl. */
const getPacificWeekday = (now) => {
    const weekday = PACIFIC_WEEKDAY_FORMATTER.format(now);
    return PACIFIC_WEEKDAY_SHORT[weekday] ?? 0;
};
/**
 * Current Pacific time as a fractional hour (e.g. 13.5 = 1:30pm). Window
 * boundaries can land on half-hours — the video channel breaks 1:00–1:30pm —
 * so the gate needs minute resolution, not just the whole hour.
 */
const getPacificHourDecimal = (now) => {
    const map = (0, exports.toNumericPartMap)(PACIFIC_HOUR_FORMATTER.formatToParts(now));
    // hour12:false renders midnight as "24" in some locales; fold it back to 0.
    const hour = map.hour === 24 ? 0 : map.hour;
    return hour + map.minute / 60;
};
/** True iff the resolved Pacific weekday and fractional hour fall within a single [start, end) range. */
const isWithinRange = ({ range, weekday, hourDecimal }) => {
    if (range.days !== undefined && !range.days.includes(weekday)) {
        return false;
    }
    return hourDecimal >= range.start && hourDecimal < range.end;
};
/**
 * True iff the current Pacific time falls within the support window. A window is
 * either one [start, end) range or several (the video channel splits around a
 * midday break); the gate is open when any range matches.
 */
const isWithinHours = (window, now = new Date()) => {
    const ranges = Array.isArray(window) ? window : [window];
    if (ranges.length === 0) {
        return false;
    }
    const weekday = getPacificWeekday(now);
    const hourDecimal = getPacificHourDecimal(now);
    return ranges.some((range) => isWithinRange({ range, weekday, hourDecimal }));
};
exports.isWithinHours = isWithinHours;
